import { queryClient } from './queryClient'
import { queryKeys } from './queryKeys'

/**
 * Helper to invalidate tenant-related queries after mutations
 * Use this when updating tenant settings, theme, or configuration
 */
export const invalidateTenantQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.settings(tenantId) })
}

export const invalidateUserQueries = (userId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.user() })
  queryClient.invalidateQueries({ queryKey: queryKeys.userSettings(userId) })
}

/**
 * Helper to invalidate booking-related queries
 * Use this after creating, updating, or deleting bookings
 */
export const invalidateBookingQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.monthlyOverview.all(tenantId) })
  queryClient.invalidateQueries({
    queryKey: [...queryKeys.customers.all(tenantId), 'bookingCounts'],
  })
  queryClient.invalidateQueries({ queryKey: queryKeys.foodEvents.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.customers.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: [...queryKeys.customers.all(tenantId), 'bookings'] })
  queryClient.invalidateQueries({ queryKey: queryKeys.tableUtilization(tenantId) })
}

/**
 * Helper to invalidate calendar-related queries
 * Use this after updating rooms, resources, or room order
 */
export const invalidateCalendarQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all(tenantId) })
}

/**
 * Helper to invalidate employee-related queries
 * Use this after updating employees or their absences
 */
export const invalidateEmployeeQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.employees.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
}

/**
 * Helper to invalidate service-related queries
 * Use this after updating services or service categories
 */
export const invalidateServiceQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.services.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
}

/**
 * Helper to invalidate room-related queries
 * Use this after updating rooms or room settings
 */
export const invalidateRoomQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
}

/**
 * Helper to invalidate shift-related queries
 * Use this after updating shifts or shift settings
 */
export const invalidateShiftQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
}

/**
 * Helper to invalidate monthly overview-related queries
 * Use this after creating, updating, or deleting bookings, daily notes, or calendar events
 */
export const invalidateMonthlyOverviewQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.monthlyOverview.all(tenantId) })
}

/**
 * Helper to invalidate customer-related queries
 * Use this after creating, updating, or deleting customers
 */
export const invalidateCustomerQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.customers.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.monthlyOverview.all(tenantId) })
  // Customer updates affect bookings that contain customer information
  queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all(tenantId) })
}

/**
 * Helper to invalidate customer changelog-related queries
 * Use this after creating, updating, or deleting changelog entries
 */
export const invalidateCustomerChangelogQueries = (bookingId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.changelog(bookingId) })
}

/**
 * Helper to invalidate food event-related queries
 * Use this after creating, updating, or deleting food events
 */
export const invalidateFoodEventQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.foodEvents.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all(tenantId) })
  // Food events affect calendar views
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.monthlyOverview.all(tenantId) })
}

/**
 * Helper to invalidate daily note-related queries
 * Use this after creating, updating, or deleting daily notes
 */
export const invalidateDailyNoteQueries = (tenantId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.dailyNotes.all(tenantId) })
  // Daily notes might affect calendar views
  queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all(tenantId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.monthlyOverview.all(tenantId) })
}

/**
 * Generic helper for optimistic updates with rollback
 *
 * @param queryKey - The query key to update
 * @param updater - Function to update the cached data
 * @returns Context with previous value for rollback
 *
 * @example
 * ```typescript
 * const mutation = useMutation({
 *   mutationFn: updateSettings,
 *   onMutate: async (newSettings) => {
 *     return createOptimisticUpdate(
 *       queryKeys.tenant(tenantId),
 *       (old) => ({ ...old, ...newSettings })
 *     )
 *   },
 *   onError: (err, variables, context) => {
 *     rollbackOptimisticUpdate(queryKeys.tenant(tenantId), context)
 *   }
 * })
 * ```
 */
export const createOptimisticUpdate = <T>(
  queryKey: readonly unknown[],
  updater: (old: T | undefined) => T
) => {
  // Cancel outgoing refetches to prevent race conditions
  queryClient.cancelQueries({ queryKey })

  // Snapshot previous value
  const previous = queryClient.getQueryData<T>(queryKey)

  // Optimistically update to new value
  queryClient.setQueryData<T>(queryKey, updater)

  // Return context with previous value for potential rollback
  return { previous }
}

/**
 * Rollback helper for optimistic updates
 *
 * @param queryKey - The query key to rollback
 * @param context - Context object with previous value from createOptimisticUpdate
 *
 * @example
 * ```typescript
 * onError: (err, variables, context) => {
 *   rollbackOptimisticUpdate(queryKeys.tenant(tenantId), context)
 *   showToast({ severity: 'error', summary: 'Update failed' })
 * }
 * ```
 */
export const rollbackOptimisticUpdate = <T>(
  queryKey: readonly unknown[],
  context: { previous: T | undefined } | undefined
) => {
  if (context?.previous !== undefined) {
    queryClient.setQueryData(queryKey, context.previous)
  }
}

/**
 * Helper to prefetch a query
 * Use this to improve perceived performance by loading data before it's needed
 *
 * @example
 * ```typescript
 * // Prefetch booking details when hovering over a row
 * onMouseEnter={() => {
 *   prefetchQuery(
 *     queryKeys.bookings.detail(tenantId, bookingId),
 *     () => fetchBookingDetails(bookingId)
 *   )
 * }}
 * ```
 */
export const prefetchQuery = async <T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  staleTime?: number
) => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime,
  })
}

/**
 * Helper to manually set query data
 * Useful when you already have the data from a mutation response
 *
 * @example
 * ```typescript
 * onSuccess: (newBooking) => {
 *   // Set the detail query data from create response
 *   setQueryData(
 *     queryKeys.bookings.detail(tenantId, newBooking.id),
 *     newBooking
 *   )
 * }
 * ```
 */
export const setQueryData = <T>(queryKey: readonly unknown[], data: T) => {
  queryClient.setQueryData(queryKey, data)
}
